// services/fileService.js
import api from './api';
import encryption from './encryption';
import CryptoJS from 'crypto-js';

class FileService {
  constructor() {
    this.CHUNK_SIZE = 1024 * 1024; // 1MB chunks
    this.uploadSessions = new Map();
  }

  async calculateFileHash(file) {
    console.log('🔵 [FileService] Calculating hash for:', file.name);
    
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      const chunkSize = 1024 * 1024 * 2;
      let offset = 0;
      
      const hash = CryptoJS.algo.SHA256.create();
      
      const loadNext = () => {
        const chunk = file.slice(offset, offset + chunkSize);
        reader.readAsArrayBuffer(chunk);
      };
      
      reader.onload = (e) => {
        const wordArray = CryptoJS.lib.WordArray.create(e.target.result);
        hash.update(wordArray);
        
        offset += chunkSize;
        if (offset < file.size) {
          loadNext();
        } else {
          const finalHash = hash.finalize().toString(CryptoJS.enc.Hex);
          console.log('🟢 [FileService] Hash calculated:', finalHash);
          resolve(finalHash);
        }
      };
      
      reader.onerror = (error) => {
        console.error('🔴 [FileService] Hash calculation error:', error);
        reject(error);
      };
      
      loadNext();
    });
  }

  async initializeUpload(recipientUuid, file) {
    console.log('🔵 [FileService] Initializing upload:', {
      recipientUuid,
      fileName: file.name,
      fileSize: file.size,
      mimeType: file.type
    });
    
    try {
      const fileHash = await this.calculateFileHash(file);
      
      const payload = {
        recipientUuid,
        fileName: file.name,
        fileSize: file.size,
        mimeType: file.type || 'application/octet-stream',
        fileHash
      };
      
      console.log('🔵 [FileService] Sending initialize request:', payload);
      
      // Use api.request with /api prefix
      const response = await api.request('/api/files/initialize', {
        method: 'POST',
        body: JSON.stringify(payload)
      });
      
      console.log('🟢 [FileService] Upload initialized:', response);
      
      this.uploadSessions.set(response.fileId, {
        fileId: response.fileId,
        file,
        chunkSize: response.chunkSize || this.CHUNK_SIZE,
        uploadedBytes: 0
      });
      
      return response;
    } catch (error) {
      console.error('🔴 [FileService] Initialize error:', error);
      throw error;
    }
  }

  async uploadFile(fileId, onProgress) {
    console.log('🔵 [FileService] Starting chunked upload for:', fileId);
    
    const session = this.uploadSessions.get(fileId);
    if (!session) {
      console.error('🔴 [FileService] Upload session not found:', fileId);
      throw new Error('Upload session not found');
    }
    
    const { file, chunkSize } = session;
    const totalChunks = Math.ceil(file.size / chunkSize);
    
    console.log(`🔵 [FileService] Total chunks: ${totalChunks}, chunk size: ${chunkSize}`);
    
    // Get token from localStorage
    const sessionData = localStorage.getItem('chatSession');
    let token = '';
    if (sessionData) {
      try {
        const parsed = JSON.parse(sessionData);
        token = parsed.jwtToken || '';
      } catch (e) {
        console.error('Failed to parse session:', e);
      }
    }
    
    const API_URL = process.env.REACT_APP_API_URL || '';
    
    for (let i = 0; i < totalChunks; i++) {
      const start = i * chunkSize;
      const end = Math.min(start + chunkSize, file.size);
      const chunk = file.slice(start, end);
      
      console.log(`🔵 [FileService] Uploading chunk ${i + 1}/${totalChunks}, bytes: ${start}-${end}`);
      
      const formData = new FormData();
      const chunkName = `${file.name}.chunk${i}`;
      formData.append('file', chunk, chunkName);
      
      try {
        const response = await fetch(`${API_URL}/api/files/upload/${fileId}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          credentials: 'include',
          body: formData
        });
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || `HTTP error ${response.status}`);
        }
        
        const data = await response.json();
        
        console.log(`🟢 [FileService] Chunk ${i + 1}/${totalChunks} uploaded:`, data);
        
        session.uploadedBytes = end;
        
        if (onProgress) {
          onProgress({
            fileId,
            uploadedBytes: end,
            totalBytes: file.size,
            progress: (end / file.size) * 100,
            chunk: i + 1,
            totalChunks
          });
        }
      } catch (error) {
        console.error(`🔴 [FileService] Chunk ${i + 1} upload error:`, error);
        throw error;
      }
    }
    
    console.log('🟢 [FileService] All chunks uploaded');
  }

  async completeUpload(fileId) {
    console.log('🔵 [FileService] Completing upload:', fileId);
    
    try {
      const response = await api.request(`/api/files/complete/${fileId}`, {
        method: 'POST'
      });
      
      console.log('🟢 [FileService] Upload completed:', response);
      
      this.uploadSessions.delete(fileId);
      
      return response;
    } catch (error) {
      console.error('🔴 [FileService] Complete error:', error);
      throw error;
    }
  }

  async uploadFullFile(recipientUuid, file, onProgress) {
    console.log('🔵 [FileService] Starting full upload flow');
    
    try {
      // Step 1: Initialize
      console.log('🔵 [FileService] Step 1: Initialize');
      const initResult = await this.initializeUpload(recipientUuid, file);
      const { fileId } = initResult;
      
      // Step 2: Upload chunks
      console.log('🔵 [FileService] Step 2: Upload chunks');
      await this.uploadFile(fileId, (progress) => {
        if (onProgress) {
          onProgress({ ...progress, stage: 'uploading' });
        }
      });
      
      // Step 3: Complete
      console.log('🔵 [FileService] Step 3: Complete');
      if (onProgress) {
        onProgress({ stage: 'encrypting', progress: 100 });
      }
      
      const completeResult = await this.completeUpload(fileId);
      
      if (onProgress) {
        onProgress({ stage: 'complete', progress: 100 });
      }
      
      console.log('🟢 [FileService] Full upload flow complete');
      
      return {
        fileId,
        fileName: file.name,
        fileSize: file.size,
        mimeType: file.type,
        ...completeResult
      };
      
    } catch (error) {
      console.error('🔴 [FileService] Full upload failed:', error);
      throw error;
    }
  }

  async getFileInfo(fileId) {
    console.log('🔵 [FileService] Getting file info:', fileId);
    
    try {
      const response = await api.request(`/api/files/info/${fileId}`);
      console.log('🟢 [FileService] File info:', response);
      return response;
    } catch (error) {
      console.error('🔴 [FileService] Get file info error:', error);
      throw error;
    }
  }

  async downloadFile(fileId, onProgress) {
    console.log('🔵 [FileService] Downloading file:', fileId);
    
    const sessionData = localStorage.getItem('chatSession');
    let token = '';
    if (sessionData) {
      try {
        const parsed = JSON.parse(sessionData);
        token = parsed.jwtToken || '';
      } catch (e) {
        console.error('Failed to parse session:', e);
      }
    }
    
    const API_URL = process.env.REACT_APP_API_URL || '';
    
    const response = await fetch(`${API_URL}/api/files/download/${fileId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      credentials: 'include'
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP error ${response.status}`);
    }
    
    const metadata = {
      fileId: response.headers.get('X-File-Id'),
      encryptedKey: response.headers.get('X-File-Encrypted-Key'),
      iv: response.headers.get('X-File-IV'),
      authTag: response.headers.get('X-File-Auth-Tag'),
      fileName: decodeURIComponent(response.headers.get('X-File-Name') || 'file'),
      mimeType: response.headers.get('X-File-MimeType'),
      fileSize: parseInt(response.headers.get('X-File-Size') || '0'),
      fileHash: response.headers.get('X-File-Hash')
    };
    
    const blob = await response.blob();
    
    console.log('🟢 [FileService] File downloaded with metadata:', metadata);
    
    return { blob, metadata };
  }

async decryptFile(encryptedBlob, metadata, privateKey) {
  console.log('🔵 [FileService] Decrypting file...');
  console.log('🔵 [FileService] Metadata:', {
    hasEncryptedKey: !!metadata.encryptedKey,
    hasIV: !!metadata.iv,
    hasAuthTag: !!metadata.authTag,
    encryptedSize: encryptedBlob.size
  });
  
  try {
    // 1. Decrypt the AES key using RSA
    const JSEncrypt = (await import('jsencrypt')).default;
    const decryptRSA = new JSEncrypt();
    decryptRSA.setPrivateKey(privateKey);
    
    const aesKeyBase64 = decryptRSA.decrypt(metadata.encryptedKey);
    if (!aesKeyBase64) {
      throw new Error('Failed to decrypt file key - wrong private key?');
    }
    
    console.log('🟢 [FileService] AES key decrypted successfully');
    
    // 2. Convert Base64 to ArrayBuffers
    const keyBuffer = this.base64ToArrayBuffer(aesKeyBase64);
    const ivBuffer = this.base64ToArrayBuffer(metadata.iv);
    const authTagBuffer = metadata.authTag ? this.base64ToArrayBuffer(metadata.authTag) : null;
    
    // 3. Get encrypted data
    const encryptedBuffer = await encryptedBlob.arrayBuffer();
    
    console.log('🔵 [FileService] Sizes:', {
      key: keyBuffer.byteLength,
      iv: ivBuffer.byteLength,
      authTag: authTagBuffer?.byteLength || 0,
      encrypted: encryptedBuffer.byteLength
    });
    
    // 4. Import key
    const cryptoKey = await window.crypto.subtle.importKey(
      'raw',
      keyBuffer,
      { name: 'AES-GCM' },
      false,
      ['decrypt']
    );
    
    // 5. Decrypt with Web Crypto API
    // Pass auth tag separately in the algorithm parameters
    const decryptedBuffer = await window.crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv: ivBuffer,
        tagLength: 128,
        additionalData: authTagBuffer  // Pass auth tag here
      },
      cryptoKey,
      encryptedBuffer
    );
    
    console.log('🟢 [FileService] File decrypted successfully');
    console.log('🔵 [FileService] Decrypted size:', decryptedBuffer.byteLength);
    
    return new Blob([decryptedBuffer], { type: metadata.mimeType });
    
  } catch (error) {
    console.error('🔴 [FileService] Decryption failed:', error);
    
    // Fallback: Try manual auth tag combination
    console.log('🟡 [FileService] Trying fallback with manual auth tag combination...');
    
    try {
      const JSEncrypt = (await import('jsencrypt')).default;
      const decryptRSA = new JSEncrypt();
      decryptRSA.setPrivateKey(privateKey);
      
      const aesKeyBase64 = decryptRSA.decrypt(metadata.encryptedKey);
      const keyBuffer = this.base64ToArrayBuffer(aesKeyBase64);
      const ivBuffer = this.base64ToArrayBuffer(metadata.iv);
      const encryptedBuffer = await encryptedBlob.arrayBuffer();
      
      if (metadata.authTag) {
        const authTagBuffer = this.base64ToArrayBuffer(metadata.authTag);
        const encryptedBytes = new Uint8Array(encryptedBuffer);
        const tagBytes = new Uint8Array(authTagBuffer);
        
        const combined = new Uint8Array(encryptedBytes.length + tagBytes.length);
        combined.set(encryptedBytes);
        combined.set(tagBytes, encryptedBytes.length);
        
        const cryptoKey = await window.crypto.subtle.importKey(
          'raw',
          keyBuffer,
          { name: 'AES-GCM' },
          false,
          ['decrypt']
        );
        
        const decryptedBuffer = await window.crypto.subtle.decrypt(
          { name: 'AES-GCM', iv: ivBuffer },
          cryptoKey,
          combined
        );
        
        console.log('🟢 [FileService] Fallback decryption successful!');
        return new Blob([decryptedBuffer], { type: metadata.mimeType });
      }
      
      throw error;
      
    } catch (fallbackError) {
      console.error('🔴 [FileService] Fallback also failed:', fallbackError);
      throw fallbackError;
    }
  }
}

base64ToArrayBuffer(base64) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}


  saveFile(blob, fileName) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    console.log('🟢 [FileService] File saved:', fileName);
  }

  async deleteFile(fileId) {
    console.log('🔵 [FileService] Deleting file:', fileId);
    
    try {
      const response = await api.request(`/api/files/${fileId}`, {
        method: 'DELETE'
      });
      console.log('🟢 [FileService] File deleted:', response);
      return response;
    } catch (error) {
      console.error('🔴 [FileService] Delete error:', error);
      throw error;
    }
  }

  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  getFileIcon(mimeType) {
    if (mimeType?.startsWith('image/')) return '🖼️';
    if (mimeType?.startsWith('video/')) return '🎬';
    if (mimeType?.startsWith('audio/')) return '🎵';
    if (mimeType?.includes('pdf')) return '📄';
    if (mimeType?.includes('word')) return '📝';
    if (mimeType?.includes('excel') || mimeType?.includes('spreadsheet')) return '📊';
    if (mimeType?.includes('zip')) return '📦';
    if (mimeType?.startsWith('text/')) return '📃';
    return '📎';
  }
}

export default new FileService();